import { Card, Divider, Grid, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import MetricTile from '../../../components/Dashboard/MetricTile';
import SummaryConversionRate from '../../../components/Dashboard/SummaryConversionRate';
import DashboardsCommon from '../../../components/DashboardsCommon';
import prettifiedLabel from '../../../constants/labelPrettifier';

/**
 * Conversion, structured the way At a Glance is: the headline figure first, then
 * the per-event breakdown, then the trend, then the comparison that is the
 * strongest claim the product makes.
 *
 * Conversion was previously the tail of the Engagement tab, which made that tab
 * answer two unrelated questions: "did people interact" and "did that turn into
 * money". The rates in particular sat below three screens of click counts.
 */
const ConversionTab = ({ stats, prevStats, periods }) => {
  const eventTotals = stats.conversionEventBreakdown?.totals?.purchaseEvents || [];
  const prevEventTotals = prevStats.conversionEventBreakdown?.totals?.purchaseEvents || [];
  const prevEventsById = new Map(prevEventTotals.map((event) => [event.id, event.count]));

  const total = eventTotals.reduce((sum, event) => sum + event.count, 0);
  const prevTotal = prevEventTotals.reduce((sum, event) => sum + event.count, 0);

  // One tile per mapped event only when there is more than one. A domain mapping a
  // single purchase action would otherwise see the same number twice: once as the
  // total and once as its only component.
  const showEventBreakdown = eventTotals.length > 1;

  const trend = stats.conversionEventBreakdown?.purchaseEvents || [];

  return (
    <Stack gap="md" mt="md">
      <Card withBorder radius="md" padding="md">
        <Title order={5}>Conversions</Title>
        <Text size="sm" c="dimmed">
          The events this domain counts as a conversion, however they were reached.
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mt="md">
          <MetricTile big label="Conversions" value={total} previousValue={prevTotal} />
        </SimpleGrid>

        {showEventBreakdown && (
          <>
            <Divider
              my="lg"
              labelPosition="left"
              label={
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  By event
                </Text>
              }
            />
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
              {eventTotals.map((event) => (
                <MetricTile
                  key={event.id}
                  label={prettifiedLabel(event.id)}
                  value={event.count}
                  previousValue={prevEventsById.get(event.id)}
                />
              ))}
            </SimpleGrid>
          </>
        )}

        {trend.length > 0 && (
          <>
            <Divider my="lg" />
            <DashboardsCommon stats={trend} lineStacked={false} expand={true} type="row" />
          </>
        )}
      </Card>

      <Card withBorder radius="md" padding="md">
        <Title order={5}>Does engaging with Releva correlate with buying?</Title>
        <Text size="sm" c="dimmed" mb="md">
          The rate for people who engaged with a Releva surface, against those who did not.
        </Text>
        <Grid>
          <SummaryConversionRate periods={periods} />
        </Grid>
      </Card>
    </Stack>
  );
};

export default ConversionTab;
